import React, { useState } from 'react';
import { CheckCircle, Send } from 'lucide-react';
import { BottomSheet, ErrorMsg } from './UI';
import { useAuth } from '../store/auth';
import { supabase } from '../lib/supabase';

export const FeedbackSheet: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const { user, profile } = useAuth();
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const handleClose = () => {
    setMessage('');
    setError('');
    setSent(false);
    onClose();
  };

  const handleSend = async () => {
    if (message.trim().length < 5) return;
    setSending(true);
    setError('');
    try {
      const { error: fnError } = await supabase.functions.invoke('send-feedback', {
        body: { message: message.trim() },
      });
      if (fnError) throw fnError;
      setSent(true);
      setMessage('');
    } catch (_err) {
      setError('Noe gikk galt. Prøv igjen.');
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  return (
    <BottomSheet title="Send tilbakemelding" onClose={handleClose}>
      {sent ? (
        <div style={{ textAlign: 'center', padding: '36px 16px 28px' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
            <div style={{ width: 88, height: 88, borderRadius: '50%', background: 'var(--green-light)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CheckCircle size={52} color="var(--green)" strokeWidth={1.8} />
            </div>
          </div>
          <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--text)', marginBottom: 12 }}>
            Tusen takk for din tilbakemelding!
          </div>
          <div style={{ fontSize: 15, color: 'var(--text2)', lineHeight: 1.6, maxWidth: 280, margin: '0 auto 32px' }}>
            Vi setter utrolig stor pris på at du tar deg tid til å hjelpe oss med å gjøre Toolshare.no bedre for alle.
          </div>
          <button
            onClick={handleClose}
            style={{ width: '100%', padding: '15px', borderRadius: 14, background: 'var(--green)', color: '#fff', fontSize: 16, fontWeight: 700, border: 'none', cursor: 'pointer' }}
          >
            Tilbake til appen
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ background: 'var(--bg)', borderRadius: 10, padding: '12px 14px', fontSize: 14, color: 'var(--text2)', lineHeight: 1.5 }}>
            Savner du noe? Har du en idé til forbedring? Skriv det her — det går rett til oss!
          </div>

          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text3)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Fra</div>
              <div style={{ fontSize: 14, color: 'var(--text2)', fontWeight: 500 }}>{profile?.full_name || '—'}</div>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text3)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>E-post</div>
              <div style={{ fontSize: 14, color: 'var(--text2)', fontWeight: 500, wordBreak: 'break-all' }}>{user?.email || '—'}</div>
            </div>
          </div>

          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text3)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Din melding</div>
            <textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder="Skriv din tilbakemelding her..."
              rows={5}
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: '12px 14px', borderRadius: 10, fontSize: 15,
                border: '1.5px solid var(--border-strong)',
                background: 'var(--surface)', color: 'var(--text)',
                fontFamily: 'inherit', resize: 'vertical', lineHeight: 1.5,
                outline: 'none',
              }}
            />
            <div style={{ fontSize: 12, marginTop: 4, display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'var(--accent)', fontWeight: 500, opacity: message.trim().length < 5 ? 1 : 0, transition: 'opacity 0.2s' }}>
                Skriv litt til...
              </span>
              <span style={{ color: 'var(--text3)' }}>{message.length} / 2000</span>
            </div>
          </div>

          {error && <ErrorMsg msg={error} />}

          <button
            onClick={handleSend}
            disabled={sending || message.trim().length < 5}
            style={{
              width: '100%', padding: '15px', borderRadius: 14,
              background: (sending || message.trim().length < 5) ? 'var(--border-strong)' : 'var(--green)',
              color: (sending || message.trim().length < 5) ? 'var(--text3)' : '#fff',
              fontSize: 16, fontWeight: 700, border: 'none',
              cursor: (sending || message.trim().length < 5) ? 'default' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
              transition: 'background 0.2s',
            }}
          >
            {sending ? (
              'Sender...'
            ) : (
              <>
                <Send size={18} strokeWidth={2} />
                Send tilbakemelding
              </>
            )}
          </button>
        </div>
      )}
    </BottomSheet>
  );
};
